import React, {
  useRef, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';

import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskType,
  TaskMode,
} from '@heygen/streaming-avatar';

import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import IconWithText from 'components/Widgets/IconWithText';
import {
  Button,
} from 'react-bootstrap5';

import { useContext as useUsersContext } from 'components/Users/Context';
import { useContext } from 'components/AiChats/Context';
import AudioWaveIcon from 'images/new_ui/icons/audio-wave.gif';
import DotsAnimation from 'images/new_ui/icons/dots-loading.gif';
import Loading from './Loading';

import useSessionId from './Hooks/useSessionId';

const waitText = [
  'Let me think about that for a moment',
  'I want to give you a thoughtful response, just a moment',
  "I'm processing what you've shared with me",
  'Give me just a second to consider all aspects of this',
  'Let me gather my thoughts on this',
  "I'm working through this with you",
  'Taking a moment to make sure I give you the best response',
  'Let me process this thoughtfully',
  "I'm considering what you've shared",
  'Just organizing my thoughts here',
];

export default function Content({
  token, onClose, onCloseByTimeout, welcomeMessage,
}) {
  const videoRef = useRef(null);
  const mediaStream = useRef(null);
  const ws = useRef(null);
  const chunksRef = useRef([]);

  const [avatar, setAvatar] = useState(new StreamingAvatar({ token }));
  const [sessionData, setSessionData] = useState(null);

  const [streamReady, setStreamReady] = useState(false);

  const [isMicrophoneAvailable, setMicrophoneAvailable] = useState(false);
  const [chatId, setChatId] = useState(null);

  const [answerText, setAnswerText] = useState('');
  const [time, setTime] = useState(10 * 60);
  const [startSession, setStartSession] = useState(false);
  const [voiceEnable, setVoiceEnable] = useState(false);

  const [isFirstAnswerReady, setFirstAnswerReady] = useState(welcomeMessage === '');

  const [statusText, setStatusText] = useState('');

  const { state: stateUsers } = useUsersContext();
  const { dispatch, actions, state } = useContext();

  const member = stateUsers.user.attributes.specific;

  const sessionId = useSessionId({
    token: state.token,
    avatarId: state.avatar.id,
    avatarName: state.avatar.attributes.name,
    bedrockAvatarId: state.avatar.attributes.bedrock_agent,
    bedrockAvatarAliasId: state.avatar.attributes.bedrock_alias,
  });

  const {
    transcript, resetTranscript,
  } = useSpeechRecognition();
  const pauseTimer = useRef(null);
  const notificationTimer = useRef(null);
  const thinkingTimer = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [countNotifications, setCountNotifications] = useState(0);

  const sessionAttributes = {
    environment: stateUsers.user.attributes.environment,
    user_id: stateUsers.user.id,
    user_email: member.email,
    user_name: member.first_name,
    user_full_name: member.full_name,
  };

  const avatarSpeak = (text) => {
    console.log(text);
    setVoiceEnable(false);
    avatar.speak({
      sessionId: sessionData.session_id,
      text,
      taskType: TaskType.REPEAT,
      taskMode: TaskMode.SYNC,
    });
  };

  const streamText = async (message, saveMessage) => {
    setIsProcessing(true);

    const payload = {
      action: 'sendMessage',
      session_id: sessionId,
      save: saveMessage,
      message,
      session_attributes: JSON.stringify(sessionAttributes),
    };

    if (state.avatar.attributes.is_inline) {
      payload.inline = true
    }

    ws.current.send(JSON.stringify(payload));
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const terminateAvatarSession = () => {
    if (!avatar || !sessionData) return;

    avatar.stopAvatar();
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setAvatar(null);
  };

  const stopVoiceRecord = () => {
    // setIsProcessing(false);
    SpeechRecognition.stopListening();
    console.log('avatar start talk');
    setVoiceEnable(false);
  };

  const closeVideo = () => {
    terminateAvatarSession();
    stopVoiceRecord();
    onClose();
  };

  const handleStreamReady = (event) => {
    if (event.detail && videoRef.current) {
      videoRef.current.srcObject = event.detail;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().catch(console.error);
      };
      setStreamReady(true);
    } else {
      console.error('Stream is not available');
    }
  };

  // Handle stream disconnection
  const handleStreamDisconnected = () => {
    console.log('Stream disconnected');
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startVoiceRecord = () => {
    navigator.mediaDevices
      .getUserMedia({ video: false, audio: true })
      .then((stream) => {
        mediaStream.current = stream;
        SpeechRecognition.startListening({ continuous: true });
        setMicrophoneAvailable(true);
        console.log('voice enabled');
        setVoiceEnable(true);
        if (notificationTimer.current) {
          clearTimeout(notificationTimer.current);
        }
      })
      .catch((_err) => {
        // alert(`you got an error: ${err}`);
      });
  };

  const closeVideoChat = (type) => {
    terminateAvatarSession();
    stopVoiceRecord();
    onCloseByTimeout(type);
  };

  const sendNotification = () => {
    if (countNotifications === 0) {
      avatarSpeak('Are you still there?');
      setCountNotifications((prev) => prev + 1);
    } else {
      closeVideoChat('inactive');
    }
  };

  useEffect(() => {
    if ((voiceEnable || !isFirstAnswerReady) && answerText !== '') {
      avatarSpeak(answerText);
      setAnswerText('');
      if (!startSession) {
        setStartSession(true);
      }
      if (!isFirstAnswerReady) {
        setFirstAnswerReady(true);
      }
    }
  }, [voiceEnable, answerText]);

  useEffect(() => {
    if (sessionId) {
      actions.setCurrentChatId(dispatch, sessionId);
      actions.load(dispatch, state.token, stateUsers.user.attributes.environment, stateUsers.user.id);

      ws.current = new WebSocket(gon.ai_wss_api_base_url);

      ws.current.onopen = () => {
        console.log('Websocket connected');
      };

      ws.current.onmessage = (event) => {
        if (event.data !== '') {
          const data = JSON.parse(event.data);
          if (data.chunk) {
            chunksRef.current.push(data.chunk);
          } else if (data.done) {
            setIsProcessing(false);
            setAnswerText(chunksRef.current.join(' '));

            chunksRef.current = [];
            if (data.is_title_needed === '1') {
              actions.generateChatTitle(dispatch, state.token, data.chat_id);
            } else {
              actions.updateChatTitle(dispatch, data.chat_id, data.chat_title);
            }
          }
        }
      };
    }

    return () => {
      ws.current?.close();
    };
  }, [sessionId]);

  useEffect(() => {
    if (avatar) {
      console.log('init avatar');
      avatar.on(StreamingEvents.STREAM_READY, handleStreamReady);
      avatar.on(StreamingEvents.STREAM_DISCONNECTED, handleStreamDisconnected);
      avatar.on(StreamingEvents.AVATAR_START_TALKING, stopVoiceRecord);
      avatar.on(StreamingEvents.AVATAR_STOP_TALKING, startVoiceRecord);
      setSessionData(avatar.createStartAvatar({
        quality: AvatarQuality.High,
        avatarName: state.avatar.attributes.heygen,
        // disableIdleTimeout: true,
      }));

      navigator.mediaDevices
        .getUserMedia({ video: false, audio: true })
        .then((_stream) => {
          // SpeechRecognition.startListening({ continuous: true });
          setMicrophoneAvailable(true);
        })
        .catch((_err) => {
          // alert(`you got an error: ${err}`);
        });
    }
  }, [avatar]);

  useEffect(() => {
    if (sessionData && streamReady && isFirstAnswerReady) {
      if (welcomeMessage === '') {
        avatarSpeak(`Hi ${member.first_name}! It’s great to see you. What’s on your mind today?`);
        setStartSession(true);
      }
    }
  }, [sessionData, streamReady, isFirstAnswerReady]);

  useEffect(() => {
    // start calculate session interval
    const timer = setInterval(() => {
      if (startSession) {
        setTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [startSession]);

  useEffect(() => {
    // session time finish
    if (time === 0) {
      closeVideoChat('timeout');
    }
  }, [time]);

  const handlePauseDetection = () => {
    if (transcript.trim() && !isProcessing) {
      const question = transcript.trim();
      resetTranscript(); // Clear transcript after processing
      streamText(question, true);
      setCountNotifications(0);
    }
  };

  const thinkingPauseDetection = () => {
    const randomWaitText = waitText[Math.floor(Math.random() * waitText.length)];
    setAnswerText(randomWaitText);
  };

  useEffect(() => {
    if (thinkingTimer.current) {
      clearTimeout(thinkingTimer.current);
    }

    // Start a new timer when transcript is updated
    if (statusText === 'Thinking' && startSession) {
      thinkingTimer.current = setTimeout(thinkingPauseDetection, 4500); // 3-second pause threshold
    }

    return () => {
      if (thinkingTimer.current) {
        clearInterval(thinkingTimer.current);
      }
    };
  }, [statusText, startSession]);

  useEffect(() => {
    // Clear and reset timer whenever new transcript is updated
    if (pauseTimer.current) {
      clearTimeout(pauseTimer.current);
    }

    // Start a new timer when transcript is updated
    if (transcript) {
      pauseTimer.current = setTimeout(handlePauseDetection, 2000); // 3-second pause threshold
    }
  }, [transcript]);

  useEffect(() => {
    if (!voiceEnable) {
      if (notificationTimer.current) {
        clearTimeout(notificationTimer.current);
      }
    } else if (startSession && voiceEnable) {
      // check that user do not active 2 minutes
      if (notificationTimer.current) {
        clearTimeout(notificationTimer.current);
      }

      notificationTimer.current = setTimeout(() => {
        sendNotification();
      }, 60 * 1000); // 1 minutes
    }

    return () => {
      if (notificationTimer.current) {
        clearInterval(notificationTimer.current);
      }
    };
  }, [startSession, transcript, voiceEnable]);

  useEffect(() => {
    if (!isProcessing && voiceEnable) {
      setStatusText('Listening');
    } else if (isProcessing) {
      setStatusText('Thinking');
    } else {
      setStatusText(streamReady ? 'Speaking' : 'Processing');
    }
  }, [isProcessing, streamReady, voiceEnable]);

  useEffect(() => () => {
    if (mediaStream.current) {
      mediaStream.current.getAudioTracks().forEach((track) => {
        track.stop();
      });
    }
  }, []);

  useEffect(() => {
    if (sessionId && ws.current && ws.current.readyState === WebSocket.OPEN && welcomeMessage !== '') {
      streamText(welcomeMessage, false);
    }
  }, [sessionId, ws.current?.readyState]);

  return (
    <>
      <video ref={videoRef} autoPlay playsInline>
        <track kind="captions" />
      </video>

      {time > 0 && (
        <div className="timer">
          Time Left in Session:
          {' '}
          {formatTime(time)}
        </div>
      )}

      {!isMicrophoneAvailable && (
        <div className="alert-popup">
          Please grant microphone access to continue.
        </div>
      )}

      {isMicrophoneAvailable && !startSession && !isFirstAnswerReady && (
        <Loading text="Creating your personalized AI coaching experience... just a moment" />
      )}

      <div className="video-status-info">
        {statusText}
        {statusText === 'Thinking' && <img height="20px" src={DotsAnimation} alt="thinking" />}
        {statusText === 'Listening' && transcript && transcript !== ''
          && <img height="20px" src={AudioWaveIcon} alt="listening" />}
      </div>

      <div className="logo" />

      <Button
        variant="link"
        className="close-action"
        onClick={closeVideo}
      >
        <IconWithText
          iconClassName="close-circle-icon  big-icon"
        />
      </Button>
    </>
  );
}
Content.propTypes = {
  token: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseByTimeout: PropTypes.func.isRequired,
  welcomeMessage: PropTypes.string,
};

Content.defaultProps = {
  welcomeMessage: '',
};
